import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Footer from '../components/Footer'
import { fetchEmployeeNotifications, fetchUnreadCount, markNotificationAsRead, markAllAsRead } from '../services/notificationsApi'
import { fetchEmployeeAppointments, acceptAppointment, rejectAppointment } from '../services/appointmentsApi'
import { useSocket } from '../hooks/useSocket'
import { loginUser, logout, getAuthUser, getAuthToken } from '../services/authService'
import { requestNotificationPermission, registerInstallation, onMessageReceived } from '../services/fcmService'
import { apiUrl } from '../services/apiConfig'

export default function Employees() {
  const navigate = useNavigate()
  const { socket, isConnected, joinEmployeeRoom } = useSocket()
  const storedUser = getAuthUser()
  const isEmployeeUser = !!getAuthToken() && storedUser?.role === 'employee'
  const [isLoggedIn, setIsLoggedIn] = useState(isEmployeeUser)
  const [currentUser, setCurrentUser] = useState(isEmployeeUser ? storedUser : null)
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [error, setError] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  
  // Notifications and appointments state
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [appointments, setAppointments] = useState([])
  const [loading, setLoading] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [selectedAppointment, setSelectedAppointment] = useState(null)
  const [rejectionReason, setRejectionReason] = useState('')
  const [showNotifications, setShowNotifications] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedDetailsAppointment, setSelectedDetailsAppointment] = useState(null)

  // Employee ID from employees collection (set after login)
  const [employeeId, setEmployeeId] = useState(
    isEmployeeUser ? storedUser.employeeDocumentId || storedUser.employee_document_id || storedUser.id : null
  )

  useEffect(() => {
    if (isLoggedIn && employeeId) {
      loadNotifications()
      loadUnreadCount()
      loadAppointments()
      joinEmployeeRoom(employeeId)
      
      // Register for FCM notifications
      requestNotificationPermission().then(granted => {
        if (granted) {
          registerInstallation(employeeId, 'employee')
        }
      })
      
      // Refresh when a push arrives (it is shown in the notification panel by the service worker)
      return onMessageReceived((payload) => {
        console.log('FCM message received:', payload)
        loadNotifications()
        loadUnreadCount()
      })
    }
  }, [isLoggedIn, employeeId])

  // Socket event listeners
  useEffect(() => {
    if (!socket || !isConnected) return

    const handleNewNotification = (notification) => {
      setNotifications(prev => [notification, ...prev])
      setUnreadCount(prev => prev + 1)
    }

    const handleNotificationRead = (notification) => {
      setNotifications(prev => prev.map(n => n.id === notification.id ? notification : n))
      setUnreadCount(prev => Math.max(0, prev - 1))
    }

    const handleNotificationsReadAll = () => {
      setUnreadCount(0)
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
    }

    const handleAppointmentAccepted = (visitor) => {
      loadAppointments()
    }

    const handleAppointmentRejected = (visitor) => {
      loadAppointments()
    }

    socket.on('new-notification', handleNewNotification)
    socket.on('notification-read', handleNotificationRead)
    socket.on('notifications-read-all', handleNotificationsReadAll)
    socket.on('appointment-accepted', handleAppointmentAccepted)
    socket.on('appointment-rejected', handleAppointmentRejected)

    return () => {
      socket.off('new-notification', handleNewNotification)
      socket.off('notification-read', handleNotificationRead)
      socket.off('notifications-read-all', handleNotificationsReadAll)
      socket.off('appointment-accepted', handleAppointmentAccepted)
      socket.off('appointment-rejected', handleAppointmentRejected)
    }
  }, [socket, isConnected])

  const loadNotifications = async () => {
    try {
      const data = await fetchEmployeeNotifications(employeeId)
      setNotifications(data)
    } catch (err) {
      console.error('Failed to load notifications:', err)
    }
  }

  const loadUnreadCount = async () => {
    try {
      const data = await fetchUnreadCount(employeeId)
      setUnreadCount(data.count)
    } catch (err) {
      console.error('Failed to load unread count:', err)
    }
  }

  const loadAppointments = async () => {
    try {
      setLoading(true)
      const data = await fetchEmployeeAppointments(employeeId)
      setAppointments(data)
    } catch (err) {
      console.error('Failed to load appointments:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId)
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n))
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (err) {
      console.error('Failed to mark as read:', err)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead(employeeId)
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      setUnreadCount(0)
    } catch (err) {
      console.error('Failed to mark all as read:', err)
    }
  }

  const handleAcceptAppointment = async (visitorId) => {
    try {
      await acceptAppointment(visitorId)
      loadAppointments()
    } catch (err) {
      console.error('Failed to accept appointment:', err)
      setError('Failed to accept appointment')
    }
  }

  const handleRejectAppointment = async () => {
    if (!rejectionReason.trim()) {
      setError('Rejection reason is required')
      return
    }
    try {
      await rejectAppointment(selectedAppointment.id, rejectionReason)
      setShowRejectModal(false)
      setRejectionReason('')
      setSelectedAppointment(null)
      loadAppointments()
      setError('')
    } catch (err) {
      console.error('Failed to reject appointment:', err)
      setError('Failed to reject appointment')
    }
  }

  const openRejectModal = (appointment) => {
    setSelectedAppointment(appointment)
    setShowRejectModal(true)
    setRejectionReason('')
  }

  const openDetailsModal = (appointment) => {
    setSelectedDetailsAppointment(appointment)
    setShowDetailsModal(true)
  }

  const closeRejectModal = () => {
    setShowRejectModal(false)
    setSelectedAppointment(null)
    setRejectionReason('')
  }

  const closeDetailsModal = () => {
    setShowDetailsModal(false)
    setSelectedDetailsAppointment(null)
  }

  const handleLogin = async (e) => {
    e.preventDefault()
    setLoginLoading(true)
    setError('')

    try {
      const result = await loginUser(formData.email, formData.password)
      if (result.user.role !== 'employee') {
        throw new Error('Invalid role. Only employees can access this page')
      }
      setIsLoggedIn(true)
      setCurrentUser(result.user)
      setFormData({ email: '', password: '' })

      // Look up the employee document ID from the employees collection by email
      const empResponse = await fetch(apiUrl('/employees/active', { search: result.user.email }))
      if (empResponse.ok) {
        const data = await empResponse.json()
        const employees = Array.isArray(data) ? data : data.employees || []
        const match = employees.find(e => e.email?.toLowerCase() === result.user.email?.toLowerCase())
        if (match) {
          setEmployeeId(match.id)
        } else {
          console.warn('No matching employee record found for:', result.user.email)
          setEmployeeId(result.user.id)
        }
      } else {
        setEmployeeId(result.user.id)
      }
    } catch (err) {
      setError(err.message || 'Invalid credentials')
    } finally {
      setLoginLoading(false)
    }
  }

  const handleLogout = () => {
    logout()
    setIsLoggedIn(false)
    setCurrentUser(null)
    setFormData({ email: '', password: '' })
    navigate('/')
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-white to-saffron-50">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md">
            <button
              onClick={() => navigate('/')}
              className="mb-6 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </button>

            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="text-center mb-8">
                <img src="/logo.png" alt="igus Logo" className="mx-auto mb-4 h-16 w-auto" />
                <h1 className="text-2xl font-bold text-gray-900">Employee Login</h1>
  <p className="mt-2 text-sm text-gray-600">Login as Employee</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your.email@igus.net"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="password" className="mb-1 block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter your password"
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200"
                    required
                  />
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loginLoading}
                  className="w-full rounded-lg bg-saffron-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-saffron-700 focus:outline-none focus:ring-2 focus:ring-saffron-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {loginLoading ? 'Logging in...' : 'Login'}
                </button>
              </form>

              <div className="mt-6 border-t border-gray-200 pt-6">
                <p className="text-xs text-gray-500 text-center">
                  <span className="block mb-2">Contact your administrator</span>
                  <span className="block">if you need assistance with login credentials</span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  const isManager = false

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-white to-saffron-50">
      <header className="bg-white shadow-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="igus Logo" className="h-10 w-auto" />
            <h1 className="text-xl font-bold text-gray-900">
              {isManager ? 'Manager Dashboard' : 'Employee Dashboard'}
            </h1>
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${isManager ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
              {isManager ? 'Manager' : 'Employee'}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Logout
          </button>
        </div>
      </header>

      <main className="flex-1 mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">
            Welcome, {isManager ? 'Manager' : 'Employee'}
          </h2>
          <p className="mt-1 text-gray-600">
            {isManager 
              ? 'Manage team, view reports, and oversee operations' 
              : 'View your appointments and manage your schedule'}
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Today's Appointments</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">5</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-saffron-100">
                <svg className="h-6 w-6 text-saffron-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending Requests</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">3</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Completed</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">12</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>

          {isManager && (
            <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Team Members</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">9</p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100">
                  <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Appointments</h3>
            <div className="space-y-3">
              {appointments.length === 0 ? (
                <p className="text-gray-500 text-sm">No appointments scheduled</p>
              ) : (
                appointments.map((appt) => (
                  <div key={appt.id} className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
                    <div>
                      <p className="font-medium text-gray-900">{appt.full_name}</p>
                      <p className="text-sm text-gray-500">{appt.purpose} • {appt.appointment_time} • {appt.organization}</p>
                      <p className="text-xs text-gray-400">Appointment date: {appt.appointment_date || 'N/A'}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 justify-end">
                      <button
                        onClick={() => openDetailsModal(appt)}
                        className="rounded-lg border border-saffron-300 px-3 py-1 text-xs font-medium text-saffron-700 hover:bg-saffron-50"
                      >
                        View Details
                      </button>
                      {appt.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleAcceptAppointment(appt.id)}
                            className="rounded-lg bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => openRejectModal(appt)}
                            className="rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {appt.status === 'accepted' && (
                        <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">Accepted</span>
                      )}
                      {appt.status === 'rejected' && (
                        <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">Rejected</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {isManager ? (
            <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Overview</h3>
              <div className="space-y-3">
                {[
                  { name: 'Mourya', department: 'Engineering', status: 'Available' },
                  { name: 'John Doe', department: 'Engineering', status: 'In Meeting' },
                  { name: 'Jane Smith', department: 'HR', status: 'Available' },
                  { name: 'Peter Jones', department: 'Sales', status: 'Available' },
                ].map((member, idx) => (
                  <div key={idx} className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
                    <div>
                      <p className="font-medium text-gray-900">{member.name}</p>
                      <p className="text-sm text-gray-500">{member.department}</p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-xs font-medium ${member.status === 'Available' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {member.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-saffron-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Schedule</h3>
              <div className="space-y-4">
                <div className="relative">
                  <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200" />
                  <div className="space-y-4">
                    <div className="relative pl-8">
                      <div className="absolute left-1.5 h-3 w-3 rounded-full bg-saffron-500" />
                      <p className="text-sm font-medium text-gray-900">09:00 AM</p>
                      <p className="text-sm text-gray-600">Team Standup</p>
                    </div>
                    <div className="relative pl-8">
                      <div className="absolute left-1.5 h-3 w-3 rounded-full bg-green-500" />
                      <p className="text-sm font-medium text-gray-900">10:30 AM</p>
                      <p className="text-sm text-gray-600">Interview with John Smith</p>
                    </div>
                    <div className="relative pl-8">
                      <div className="absolute left-1.5 h-3 w-3 rounded-full bg-yellow-500" />
                      <p className="text-sm font-medium text-gray-900">02:00 PM</p>
                      <p className="text-sm text-gray-600">Sales Meeting with Sarah Johnson</p>
                    </div>
                    <div className="relative pl-8">
                      <div className="absolute left-1.5 h-3 w-3 rounded-full bg-gray-300" />
                      <p className="text-sm font-medium text-gray-900">04:30 PM</p>
                      <p className="text-sm text-gray-600">Code Review</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="rounded-xl bg-white p-6 shadow-lg w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Appointment</h3>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Please provide a reason for rejection..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm outline-none focus:border-saffron-500 focus:ring-2 focus:ring-saffron-200 mb-4"
              rows={4}
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowRejectModal(false)
                  setRejectionReason('')
                  setSelectedAppointment(null)
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectAppointment}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visitor Details Modal */}
      {showDetailsModal && selectedDetailsAppointment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white shadow-lg">
            <div className="flex items-center justify-between border-b border-gray-200 p-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Visitor Details</h3>
                <p className="text-sm text-gray-500">Complete appointment and visitor information</p>
              </div>
              <button onClick={closeDetailsModal} className="text-gray-400 hover:text-gray-600">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="grid gap-6 p-6 md:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Visitor</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.full_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Organization</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.organization || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Phone</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Email</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Gender</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.gender || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Purpose</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.purpose || 'N/A'}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Employee to Meet</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.employee_name || 'N/A'} {selectedDetailsAppointment.employee_department ? `• ${selectedDetailsAppointment.employee_department}` : ''}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Appointment Date</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.appointment_date || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Appointment Time</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.appointment_time || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Status</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.status || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Reason</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.reason || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Notes</p>
                  <p className="mt-1 text-sm text-gray-900">{selectedDetailsAppointment.additional_notes || 'N/A'}</p>
                </div>
              </div>

              <div className="md:col-span-2">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Signature</p>
                <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  {selectedDetailsAppointment.signature ? (
                    selectedDetailsAppointment.signature.startsWith('data:image') ? (
                      <img
                        src={selectedDetailsAppointment.signature}
                        alt="Visitor signature"
                        className="max-h-40 w-full object-contain"
                      />
                    ) : (
                      <pre className="whitespace-pre-wrap break-all text-xs text-gray-700">
                        {selectedDetailsAppointment.signature}
                      </pre>
                    )
                  ) : (
                    <p className="text-sm text-gray-500">No signature available</p>
                  )}
                </div>
              </div>

              <div className="md:col-span-2 flex justify-end">
                <button
                  onClick={closeDetailsModal}
                  className="rounded-lg bg-saffron-600 px-4 py-2 text-sm font-medium text-white hover:bg-saffron-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  )
}
